import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import {
  getCreditMemoDetailsById,
  getInvoiceById,
  InvoiceDetailDTO,
  useInvoiceSlice,
  uploadInvoiceNotes,
  fetchInvoiceNotesById,
  InvoiceNotes,
  PaymentInfo,
  previewInvoice,
  stripeCheckOutPaymentData,
  changeInvoiceStatus,
  addLateFeeByInvoice,
  getInvoiceByIdLoadingGifSkipped,
  fetchLateFeeHistory,
  LateFeeHistoryDTO,
} from "../../../redux/slice/invoice/InvoiceSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { SUCCESS_CODE } from "../../../service/ServiceConstants";
import {
  AccountBalanceWalletOutlined,
  AssignmentOutlined,
  AttachFile,
  AttachMoney,
  BoltOutlined,
  CancelOutlined,
  ChatBubbleOutlineOutlined,
  CloseOutlined,
  CommentOutlined,
  CreditCard,
  CurrencyExchangeOutlined,
  DocumentScannerOutlined,
  HistoryOutlined,
  KeyboardVoice,
  ListAltOutlined,
  EditOutlined,
  ModeOutlined,
  PaymentsOutlined,
  PictureAsPdfOutlined,
  ReceiptLongOutlined,
  Send,
  TrackChanges,
  ViewInArOutlined,
} from "@mui/icons-material";
import Button from "../../common/Button";
import PageBackButton from "../../common/PageBackButton";
import { convertBase64, hideElement, priceFormatter, renderDynamicAgingColumn } from "../../../utils/Utility";
import { canShowUpdateInvoice } from "../../../utils/invoiceUpdateVisibility";
import { buildActivityCenterPath } from "../../../utils/activityCenterUtils";
import PDFViewer from "../../common/pdf/PDFViewer";
import { fetchOrderDocumentsByIds, getOrderItemsByInvoiceId, resetOrderDocumentInfo, resetOrderItemListInfo, useOrderSlice } from "../../../redux/slice/order/OrderSlice";
import CustomIconButton from "../../common/CustomIconButton";
import CreditMemoTable from "../../common/CreditMemoTablePopup";
// import CustomCommentPopup from "../../common/CustomCommentPopup";
import CustomCommentPreview from "../../common/CustomCommentPreview";
import { useNavigation } from "../../../hooks/useNavigation";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { selectIsTokenAvailable, selectIsValueFoundInToken, setSelectedModule, useAuthSlice } from "../../../redux/slice/authSlice";
import { AUDIT_ENTITYS, CAN_ACCESS_APPLICATION, COMMISSION_STATUS, INVOICE_ACTION, INVOICE_DETAIL_VIEW_ACTIONS, INVOICE_STATUS, ORDER_AVAILABILITY, PAYMENT_STATUS, USER_ROLE_ENTITY_NAMES } from "../../../constants/applicationConstants";
import CustomTextField from "../../common/CustomTextField";
import { useForm } from "react-hook-form";
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import CustomProgress from "../../common/CustomProgress";
import SpeechToTextPopup from "../../common/SpeechToTextPopup";
import ExcelDetailsPopup from "../orders/ExcelDetailsPopup";
import { getSalesDetailsByInvoiceId, resetSaleDetailsStatus, useSaleDetailsSlice } from "../../../redux/slice/saleDetails/SaleDetailsSlice";
import StripePaymentTypePopup from "../payment/stripe/StripePaymentTypePopup";
import { formatMethodLabel } from "../payment/PaymentList";
import { renderPaymentStatusColor } from "../payment/PaymentDetails";
import CustomCommentPopup from "../../common/CustomCommentPopup";
import ActionMenu, { ActionItem } from "../../common/ActionMenu";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import LateFeePopup from "./LateFeePopup";
import LateFeeSummaryPopup from "./LateFeeSummaryPopup";
import { fetchDocumentById } from "../../../redux/slice/practiceSlice";
import { SectionHeader } from "../../ui/ContentSection";
import { useModalStyles } from "../../common/useModalStyles";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { AgencyPayoutInvoice, RecordPayoutForm } from "./RecordPayout";
import { OverridePayoutInvoice, RecordPayoutOverrideForm } from "./RecordPayoutOverride";
import EditCommissionPopup from "../orders/EditCommissionPopup";
import {
  invoiceHasServiceFeePayment,
  resolveServiceFeeAmountEdit,
  saveServiceFeeAmountEdit,
  ServiceFeeAmountEditTarget,
  ServiceFeeKind,
} from "../../../utils/serviceFeeAmountEdit";

type ModalTokens = {
  border: string;
  bg: string;
  textPrimary: string;
  textSecondary: string;
  accent: string;
  error: string;
  headerFooterBg: string;
};

const InfoRow = ({
  label,
  value,
  highlight = false,
  mTokens,
}: {
  label: string;
  value?: React.ReactNode;
  highlight?: boolean;
  mTokens: ModalTokens;
}) => (
  <Box display="flex" alignItems="center" py={0.55} sx={{ borderBottom: `1px solid ${mTokens.border}`, "&:last-child": { borderBottom: "none" } }}>
    <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, width: "45%", flexShrink: 0, fontWeight: highlight ? 700 : 400, pt: 0.15 }}>
      {label}
    </Typography>
    <Box
      sx={{
        fontSize: highlight ? "1rem" : "0.88rem",
        color: highlight ? "#10B981" : mTokens.textPrimary,
        fontWeight: highlight ? 800 : 600,
        flex: 1,
        minWidth: 0,
      }}
    >
      {value ?? "—"}
    </Box>
  </Box>
);

const BillingDetail = ({
  embedded = false,
  invoiceIdOverride,
}: {
  /** When true, hide page Back/title — parent (e.g. Finance HQ) already provides chrome. */
  embedded?: boolean;
  /** Prefer this id when hosted outside /billing_detail route (same component, no fork). */
  invoiceIdOverride?: number;
} = {}) => {
  const location = useLocation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const {
    tokens: mTokens,
    isDark: isDarkModal,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    closeIconSx,
  } = useModalStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden",
    boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };
  const outlineAccentBtnSx = {
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.82rem",
    borderRadius: "10px",
    height: 36,
    px: 1.6,
    color: mTokens.accent,
    borderColor: mTokens.accent,
    backgroundColor: "transparent",
    whiteSpace: "nowrap" as const,
    letterSpacing: "0.04em",
    "&:hover": {
      backgroundColor: `${mTokens.accent}14`,
      borderColor: mTokens.accent,
      boxShadow: "none",
    },
    "& .MuiTypography-root": { color: `${mTokens.accent} !important` },
    "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: mTokens.accent },
  };
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetailDTO>();
  const [showPDF, setShowPDF] = useState<{ isShow: boolean, productName: string, fileName: string, content?: string }>({ isShow: false, productName: "", fileName: "", content: "" })
  //CS-1191 - Strat
  const canAccessEditInvoiceAction = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessInvoiceActions", INVOICE_ACTION.EDIT_INVOICE)(state));
  //CS-1191 - End
  const { orderItemsListInfo, orderDocumentInfo } = useOrderSlice();
  const [showCreditMemoPopup, setShowCreditMemoPopup] = useState(false);
  const hasV3CommissionRate = selectedInvoice?.v3Commissions && selectedInvoice.v3Commissions.commissionRate !== null;
  const hasAgencyCommissionRate = selectedInvoice?.agencyCommissions && selectedInvoice.agencyCommissions?.some((data: any) => data.commissionRate !== null);
  const hasOvrAgencyCommissionRate = selectedInvoice?.ovrAgencyCommissions && selectedInvoice.ovrAgencyCommissions?.some((data: any) => data.commissionRate !== null);
  const expandGridItem = !hasV3CommissionRate || !hasAgencyCommissionRate || !hasOvrAgencyCommissionRate;
  const { recordCreditMemo, invoiceNotesList, invoiceObject, lateFeeHistory } = useInvoiceSlice();
  // const [showPaymentCommentPopup, setShowPaymentCommentPopup] = useState(false);
  // const [PaymentCommentData, setPaymentCommentData] = useState<{ title: string; body: string }>({
  //   title: "",
  //   body: "",
  // });
  const canViewAgencies = useSelector((state: RootState) => selectIsValueFoundInToken("canViewAgenciesOnInvoice", INVOICE_DETAIL_VIEW_ACTIONS.AGENCY)(state));
  const canViewOvrAgencies = useSelector((state: RootState) => selectIsValueFoundInToken("canViewAgenciesOnInvoice", INVOICE_DETAIL_VIEW_ACTIONS.OVERRIDE)(state));
  //CS-1458 - Integrated stripe payment token and payment popup
  const canCreateStripePayment = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessInvoiceActions", INVOICE_ACTION.STRIPE_PAYMENT)(state));
  const [showStripePaymentTypePopup, setShowStripePaymentTypePopup] = useState(false);
  //CS-1576 - To identify the popup is opened from billing details page
  const [isStripePaymentTypePopupFromBillingDetails, setIsStripePaymentTypePopupFromBillingDetails] = useState(false);
  //CS-1212 - Start
  const { handleSubmit, setValue, getValues, control } = useForm<InvoiceNotes>();
  const userSideMap: Record<string, boolean> = {};
  let lastAuditUserName: string | null = null;
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [pdfContent, setPDFContent] = useState<string>("");
  //CS-1212 - End
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const [shippingCharges, setShippingCharges] = useState(0);
  const [total, setTotal] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [isVoicePopupOpen, setIsVoicePopupOpen] = useState(false);
  const { SalesDetailsInfo } = useSaleDetailsSlice();
  const [showExcelPopup, setShowExcelPopup] = useState(false);
  const { userInfo } = useAuthSlice();
  const query = new URLSearchParams(location.search);
  const [invoiceId, setInvoiceId] = useState<number>(0);
  //CS-1512 - added failure reason in popup
  const [failureReasonData, setFailureReasonData] = useState<{ title: string; body: string }>({ title: "", body: "" });
  const [showReasonPopup, setShowReasonPopup] = useState(false);
  const canViewExcelData = useSelector((state: RootState) => selectIsTokenAvailable("canViewExcelData")(state));
  const canAccessPaymentDetails = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.PAYMENT_MANAGEMENT)(state));
  const canAccessRecordCreditMemoAction = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessInvoiceActions", INVOICE_ACTION.CREDIT_MEMO)(state));
  const canAccessAddPaymentAction = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessInvoiceActions", INVOICE_ACTION.ADD_PAYMENT)(state));
  const canAccessCloseInvoiceAction = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessInvoiceActions", INVOICE_ACTION.CLOSE_INVOICE)(state));
  const canAuditInvoice = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessAuditAction", AUDIT_ENTITYS.INVOICEAUDIT)(state)); // CS-1532
  const canLateFeeAction = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessInvoiceActions", INVOICE_ACTION.LATE_FEE)(state));
  const canAccessActivityCenter = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessInvoiceActions", INVOICE_ACTION.ACTIVITY_CENTER)(state));
  const canAccessCreateAGYPayout = useSelector((state: RootState) => selectIsValueFoundInToken("canCreatePayOut", INVOICE_STATUS.AGENCY)(state));
  const canAccessCreateOVRPayout = useSelector((state: RootState) => selectIsValueFoundInToken("canCreatePayOut", INVOICE_STATUS.OVERRIDE)(state));
  const canEditCommissionRate = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessCommissionActions", COMMISSION_STATUS.EDIT)(state));
  const [overridePayoutInvoices, setOverridePayoutInvoices] = useState<OverridePayoutInvoice[] | null>(null);
  const [agencyPayoutInvoices, setAgencyPayoutInvoices] = useState<AgencyPayoutInvoice[] | null>(null);
  const [serviceFeeEdit, setServiceFeeEdit] = useState<ServiceFeeAmountEditTarget | null>(null);

  const [showOptionPopup, setShowOptionsPopup] = useState<OptionPopupObjectProps>({ message: "", show: false }); // CS-1532
  const [lateFees, setLateFees] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [showLateFeeSummaryPopup, setShowLateFeeSummaryPopup] = useState(false);

  //mount
  useEffect(() => {
    resetSlices();
    if (!embedded) {
      dispatch(setSelectedModule("Invoice"));
    }
    let fetchedInvoiceId: string | number | null =
      invoiceIdOverride ?? location.state?.invoiceId ?? null;
    if (fetchedInvoiceId == null) {
      fetchedInvoiceId = query.get("invoiceId");
    }

    if (fetchedInvoiceId) {
      const parsedInvoiceId = Number(fetchedInvoiceId);

      if (!isNaN(parsedInvoiceId) && parsedInvoiceId > 0) {
        setInvoiceId(parsedInvoiceId);
        setInvoiceDetails(parsedInvoiceId);
        setValue("xrefId", String(fetchedInvoiceId)); //CS-1212
        dispatch(fetchInvoiceNotesById(parsedInvoiceId)); //CS-1212
        dispatch(getSalesDetailsByInvoiceId(parsedInvoiceId));
      };
    }
    resetData(); //CS-1212
  }, [embedded, invoiceIdOverride]);

  const resetSlices = () => {
      dispatch(resetOrderDocumentInfo());
      dispatch(resetOrderItemListInfo());
  }

  //Unmount — skip shared-slice resets when embedded in Finance HQ so Summary Excel / order data is not wiped.
  useEffect(() => () => {
    if (!embedded) {
      dispatch(resetSaleDetailsStatus());
      resetSlices();
    }
    setLateFees(0);
  }, [embedded]);

  const setInvoiceDetails = async (invoiceId: number) => {
    dispatch(getCreditMemoDetailsById(invoiceId));
    dispatch(getOrderItemsByInvoiceId(invoiceId));
    const response = await dispatch(getInvoiceById({invoiceId: invoiceId, isSaveAuditLog: true}));
    if (response?.payload?.code === SUCCESS_CODE && response?.payload?.data) {
      setSelectedInvoice(response.payload.data);
    }
  };

  const setInvoiceDetailsObj = async (invoiceId: number) => {
    const response = await dispatch(getInvoiceByIdLoadingGifSkipped({invoiceId: invoiceId, isSaveAuditLog: true, skipLoader: true}));
    if (response?.payload?.code === SUCCESS_CODE && response?.payload?.data) {
      setSelectedInvoice(response.payload.data);
    }
  };

  // const handleActionDetails = () => {
  //   const oNumber = invoiceObject?.orderNumber;
  //   if (oNumber?.startsWith("M")) {
  //     navigate("/migrate_billing_detail", { state: { invoiceId: invoiceObject?.invoiceId, orderId: invoiceObject?.orderId } });
  //   } else {
  //     navigate("/billing_detail", { state: { invoiceId: invoiceObject?.invoiceId } });
  //   }
  // };

  const handleChangeStatusToClosed = async () => {
    const resp = await dispatch(
      changeInvoiceStatus({
        data: {
          invoiceId: invoiceObject?.invoiceId ?? 0,
          mfgInvoiceStatus: "CLOSED",
        },
        interceptorOption: {},
      })
    );
    if (resp.meta.requestStatus === "fulfilled") {
      const alertObj: SingleAlertInfo = {
        message: `Invoice ${invoiceObject?.mfgInvoiceNumber} closed successfully`,
        alertType: "success",
      };
      dispatch(setSingleAlertObj(alertObj));
      await dispatch(getInvoiceById({invoiceId: invoiceId, isSaveAuditLog: true}));
    } else {
      const alertObj: SingleAlertInfo = {
        message: "Something went wrong",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
    }
    setShowOptionsPopup({ message: "", show: false });
  };

  const handleCloseInvoice = () => {
    setShowOptionsPopup({
      message: `Do you really want to close this invoice?`,
      show: true,
      variant: "confirm",
      buttons: [
        {
          name: "Confirm",
          color: "primary",
          onClick: () => handleChangeStatusToClosed(),
        },
        {
          name: "Cancel",
          color: "secondary",
          onClick: () => setShowOptionsPopup({ message: "", show: false }),
        },
      ],
    });
  };

  const handleClickInvoiceAudit = () => {
    if (invoiceObject && invoiceObject?.mfgInvoiceNumber) {
      const invoiceAuditData = {
        entityName: "INVOICE",
        entiyNumber: invoiceObject.mfgInvoiceNumber,
      };
      const url = `/auditlog?entityName=${invoiceAuditData.entityName}&entityNumber=${invoiceAuditData.entiyNumber}`;
      window.open(url, "_blank");
    }
  };

  const handleClickLateFee = () => {
    setIsPopupOpen(true);
  };

  const handleLateFeeSummaryClick = async () => {
    if (invoiceId) {
      await dispatch(fetchLateFeeHistory(invoiceId));
      setShowLateFeeSummaryPopup(true);
    }
  };

  const handleOpenPaymentInstructionDoc = async (row: InvoiceDetailDTO) => {
    const actionResult = await dispatch(fetchDocumentById(row.paymentInstructionValue ?? ""));
    const documentData = actionResult.payload;

    if (documentData && documentData.content) {
      setShowPDF({ isShow: true, productName: "Payment Instruction - " + row.mfgName, fileName: "Payment Instruction_" + row.mfgName, content: documentData.content }); //CS-1651 
    };
  }

  //function to check the paymentInstructionValue is url or document id
  const isPaymentInstructionUrl = (val: string) : boolean => {
    return val?.startsWith('https://') || val?.startsWith('http://') ? true : false;
  }

  const agencyDisplayName = (name?: InvoiceDetailDTO["agencyName"] | null) => {
    if (Array.isArray(name)) {
      const joined = name.map((part) => String(part || "").trim()).filter(Boolean).join(", ");
      return joined || undefined;
    }
    const text = String(name || "").trim();
    return text || undefined;
  };

  const invoiceHasAgencyPayout = Boolean(
    invoiceObject?.agencyId ||
    agencyDisplayName(invoiceObject?.agencyName) ||
    Number(invoiceObject?.agencyCommissionAmount) > 0 ||
    Number(invoiceObject?.agencyCommissionPaidAmount) > 0
  );

  const invoiceHasOverridePayout = Boolean(
    invoiceObject?.overrideAgencyId ||
    String(invoiceObject?.overrideAgencyName || "").trim() ||
    Number(invoiceObject?.ovrAgencyCommissionAmount) > 0 ||
    Number(invoiceObject?.ovrAgencyCommissionPaidAmount) > 0
  );

  const invoiceCanEditServiceFee = invoiceHasServiceFeePayment({
    invoiceStatus: invoiceObject?.invoiceStatus,
    invoiceAmount: invoiceObject?.invoiceAmount,
    outstandingAmount: invoiceObject?.outstandingAmt,
    payments: invoiceObject?.payment?.paymentInfo,
  });

  const refreshInvoice = () => {
    if (invoiceId) void setInvoiceDetailsObj(invoiceId);
  };

  const openAgencyPayout = () => {
    if (!invoiceObject?.invoiceId) return;
    setAgencyPayoutInvoices([{
      invoiceId: Number(invoiceObject.invoiceId),
      mfgInvoiceNumber: String(invoiceObject.mfgInvoiceNumber ?? ""),
      mfgInvoiceDate: String(invoiceObject.mfgInvoiceDate ?? ""),
      invoiceAmount: Number(invoiceObject.invoiceAmount) || 0,
      invoiceStatus: String(invoiceObject.invoiceStatus ?? ""),
      agencyName: agencyDisplayName(invoiceObject.agencyName),
    }]);
  };

  const openOverridePayout = () => {
    if (!invoiceObject?.invoiceId) return;
    setOverridePayoutInvoices([{
      invoiceId: Number(invoiceObject.invoiceId),
      mfgInvoiceNumber: String(invoiceObject.mfgInvoiceNumber ?? ""),
      mfgInvoiceDate: String(invoiceObject.mfgInvoiceDate ?? ""),
      invoiceAmount: Number(invoiceObject.invoiceAmount) || 0,
      invoiceStatus: String(invoiceObject.invoiceStatus ?? ""),
      overrideAgencyName: invoiceObject.overrideAgencyName ? String(invoiceObject.overrideAgencyName) : undefined,
    }]);
  };

  const openServiceFeeEdit = async (kind: ServiceFeeKind) => {
    if (!invoiceObject?.invoiceId) return;
    const result = await resolveServiceFeeAmountEdit(
      dispatch,
      allCodeTablesValuesMap,
      {
        invoiceId: Number(invoiceObject.invoiceId),
        mfgInvoiceNumber: invoiceObject.mfgInvoiceNumber,
        orderNumber: invoiceObject.orderNumber,
        invoiceAmount: invoiceObject.invoiceAmount,
        agencyId: invoiceObject.agencyId,
        overrideAgencyId: invoiceObject.overrideAgencyId,
      },
      kind
    );
    if (!result.ok) {
      dispatch(setSingleAlertObj({ message: result.message, alertType: "error" }));
      return;
    }
    setServiceFeeEdit(result.target);
  };

  const handleServiceFeeUpdate = async (data: any) => {
    const outcome = await saveServiceFeeAmountEdit(dispatch, data, serviceFeeEdit?.label || "Service fee");
    dispatch(setSingleAlertObj({ message: outcome.message, alertType: outcome.ok ? "success" : "error" }));
    setServiceFeeEdit(null);
    if (outcome.ok && !outcome.unchanged) refreshInvoice();
  };

  const actions: ActionItem[] = [
    // {
    //   label: "Detail",
    //   icon: <ListAltOutlined fontSize="small" />,
    //   color: "info",
    //   onClick: () => handleActionDetails(),
    //   isHidden: false // Always visible
    // },
    {
      label: "Activity Center",
      icon: <HistoryOutlined fontSize="small" />,
      color: "info",
      onClick: () => navigate(buildActivityCenterPath({
        tab: "invoice",
        invoiceId: invoiceObject?.invoiceId ?? selectedInvoice?.invoiceId,
        invoiceNumber: invoiceObject?.mfgInvoiceNumber ?? selectedInvoice?.mfgInvoiceNumber,
        orderId: invoiceObject?.orderId ?? selectedInvoice?.orderId,
        orderNumber: invoiceObject?.orderNumber ?? selectedInvoice?.orderNumber,
      })),
      isHidden: !canAccessActivityCenter,
    },
    {
      label: "Record Credit Memo",
      icon: <CreditCard fontSize="small" />,
      color: "warning",
      onClick: () => navigate("/record_credit_memo", {
        state: {
          invoiceId: invoiceObject?.invoiceId,
          invNumber: invoiceObject?.mfgInvoiceNumber,
          invAmt: invoiceObject?.invoiceAmount,
          invDate: invoiceObject?.mfgInvoiceDate,
          practice: invoiceObject?.practiceName,
          agency: invoiceObject?.agencyName,
          mfr: invoiceObject?.mfgName
        }
      }),
      isHidden: !canAccessRecordCreditMemoAction ||
        invoiceObject?.invoiceStatus === 'PAID' ||
        invoiceObject?.invoiceStatus === 'CLOSED'
    },
    {
      label: "Record Payment",
      icon: <AttachMoney fontSize="small" />,
      color: "success",
      onClick: () => navigate("/record_payment", {
        state: {
          invoiceId: invoiceObject?.invoiceId,
          invNumber: invoiceObject?.mfgInvoiceNumber,
          invAmt: invoiceObject?.invoiceAmount,
          invDate: invoiceObject?.mfgInvoiceDate,
          practice: invoiceObject?.practiceName,
          agency: invoiceObject?.agencyName,
          mfr: invoiceObject?.mfgName,
          invoiceAmount: invoiceObject?.invoiceAmount,
          creditAmount: invoiceObject?.creditAmount
        }
      }),
      isHidden: !canAccessAddPaymentAction ||
        invoiceObject?.invoiceStatus === 'PAID' ||
        invoiceObject?.invoiceStatus === 'CLOSED'
    },
    {
      label: "Record OVR Payout",
      icon: <PaymentsOutlined fontSize="small" />,
      color: "secondary",
      onClick: openOverridePayout,
      isHidden: !canAccessCreateOVRPayout || !invoiceHasOverridePayout,
    },
    {
      label: "Record AGY Payout",
      icon: <PaymentsOutlined fontSize="small" />,
      buttonColor: "#0288d1",
      onClick: openAgencyPayout,
      isHidden: !canAccessCreateAGYPayout || !invoiceHasAgencyPayout,
    },
    {
      label: "Edit AGY Service Fee Amount",
      icon: <EditOutlined fontSize="small" />,
      color: "info",
      onClick: () => { void openServiceFeeEdit("AGENCY"); },
      isHidden: !canEditCommissionRate || !invoiceHasAgencyPayout || !invoiceCanEditServiceFee,
    },
    {
      label: "Edit OVR Service Fee Amount",
      icon: <EditOutlined fontSize="small" />,
      color: "secondary",
      onClick: () => { void openServiceFeeEdit("OVERRIDE"); },
      isHidden: !canEditCommissionRate || !invoiceHasOverridePayout || !invoiceCanEditServiceFee,
    },
    {
      label: "Pay Now",
      icon: <AttachMoney fontSize="small" />,
      color: invoiceObject?.manufacturerStripeConfig?.stripe_integration === 'Y' 
        ? undefined
        : 'warning',
      // CS-1682 - Stripe purple gradient color matching BulkInvoicePayment
      buttonColor: invoiceObject?.manufacturerStripeConfig?.stripe_integration === 'Y'
        ? (theme.palette.mode === 'dark' 
          ? "linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7, #ec4899)"
          : "linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7, #ec4899)"
        )
        : undefined,
      tooltipMessage: invoiceObject?.manufacturerStripeConfig?.stripe_integration === 'Y' 
        ? "Pay via Stripe" 
        : "Payment Instruction",
      onClick: () => {
        // Check if Stripe is enabled
        if (invoiceObject?.manufacturerStripeConfig?.stripe_integration === 'Y') {
          // CS-1458 - Handling zero outstanding amount case for Pay Now
          if (invoiceObject?.outstandingAmt && Number(invoiceObject?.outstandingAmt) > 0) {
            setShowStripePaymentTypePopup(true);
            setSelectedInvoice(invoiceObject);
            setIsStripePaymentTypePopupFromBillingDetails(true);
          } else {
            let errObj: SingleAlertInfo = {
              message: "This invoice currently shows 'Unpaid,' but the outstanding amount due is 0. It was likely settled by credits or adjustments, so no payment is needed.",
              alertType: "error"
            };
            dispatch(setSingleAlertObj(errObj));
          }
        } else if (invoiceObject?.hasPaymentInstruction) {
          // Stripe is disabled but payment instruction exists
          if (invoiceObject?.paymentInstructionType === 'Product Document') {
            if (invoiceObject?.paymentInstructionValue && !isPaymentInstructionUrl(invoiceObject?.paymentInstructionValue)) {
              handleOpenPaymentInstructionDoc(invoiceObject);
            } else {
              window.open(invoiceObject.paymentInstructionValue, '_blank');
            }
          } else if (invoiceObject?.paymentInstructionType === 'External URL' && invoiceObject?.paymentInstructionValue) {
            // Redirect to external link
            window.open(invoiceObject.paymentInstructionValue, '_blank');
          }
        }
      },
      isHidden: !invoiceObject?.manufactureCustomConfig ||
        !(
          (invoiceObject?.manufacturerStripeConfig?.stripe_integration === 'Y' && canCreateStripePayment) ||
          (invoiceObject?.manufacturerStripeConfig?.stripe_integration === 'N' && invoiceObject?.hasPaymentInstruction)
        ) ||
        invoiceObject?.invoiceStatus === 'PAID' ||
        invoiceObject?.invoiceStatus === 'PROCESSING' ||
        invoiceObject?.invoiceStatus === 'CLOSED'
    },
    {
      label: "Close Invoice",
      icon: <CloseOutlined fontSize="small" />,
      color: "error",
      onClick: () => handleCloseInvoice(),
      isHidden: !(canAccessCloseInvoiceAction && invoiceObject?.invoiceStatus === 'UNPAID')
    },
    {
      label: "Late Fee",
      icon: <CurrencyExchangeOutlined fontSize="small" />,
      color: "success",
      onClick: () => handleClickLateFee(),
      isHidden: !canLateFeeAction || invoiceObject?.invoiceStatus === 'PAID' || invoiceObject?.invoiceStatus === 'CLOSED'
    },
    {
      label: "Invoice Audit",
      icon: <TrackChanges fontSize="small" />,
      color: "warning",
      onClick: () => handleClickInvoiceAudit(),
      isHidden: !canAuditInvoice || invoiceObject?.mfgName === 'Migrate Manufacturer'
    }
  ];

  const hasVisibleActions = useMemo(() => {
    return actions.some(action => !action.isHidden);
  }, [actions]);

  const paymentListData = useMemo(() => {
    const rows: PaymentInfo[] = []
    selectedInvoice?.payment?.paymentInfo?.map(payment => {
      rows.push(payment);
    });
    return rows;
  }, [selectedInvoice]);

  const checkCommissionAvailable = () => {

  }

  useEffect(() => {
    if (selectedInvoice?.payment?.paymentInfo) {
      const actualPaid = selectedInvoice.payment.paymentInfo
        .filter(p => p.paymentStatus !== "VOID" && p.paymentStatus !== "FAILED" && p.paymentStatus !== "CANCELLED")
        .reduce((sum, p) => sum + (p.paymentAmount || 0), 0);
      setPaidAmount(actualPaid);
    } else if (selectedInvoice?.payment?.totalAmount) {
      setPaidAmount(parseFloat(selectedInvoice.payment.totalAmount));
    }

    if (selectedInvoice?.shippingCharges) {
      setShippingCharges(selectedInvoice.shippingCharges);
    }

    if (selectedInvoice?.lateFee) {
      setLateFees(selectedInvoice.lateFee);
    }

    if (selectedInvoice?.invoiceAmount) {
      setInvoiceAmount(selectedInvoice?.invoiceAmount)
      const discountAmount = selectedInvoice?.discountAmount || 0;
      const totalAmount = selectedInvoice?.invoiceAmount - discountAmount + selectedInvoice?.shippingCharges + selectedInvoice?.lateFee;
      setTotal(totalAmount)
    };

    setRemainingAmount(selectedInvoice?.payment?.remainingAmount ??
      selectedInvoice?.netCogsAmount ??
      0);

  }, [selectedInvoice]);

  const remainingPercentage = (totalAmount: number | null, remainingAmount: number | null, netCogsAmount: number | null) => {
    if (totalAmount && remainingAmount) {
      const remaining = remainingAmount / (totalAmount + remainingAmount) * 100;
      const percentageValue = 100 - remaining
      return percentageValue;
    } else if (remainingAmount && !totalAmount) {
      return 0;
    } else if (!remainingAmount && !totalAmount) {
      if (netCogsAmount === 0) {
        return 100;
      } else {
        return 0;
      }
    } else {
      return 100;
    }
  };

  const remainingInvoicePaymentAmount = useCallback((netCogsAmount: number, remainingAmount: number) => {
    if (remainingAmount) {
      return priceFormatter(remainingAmount);
    }
    else if (remainingAmount === null) {
      return priceFormatter(netCogsAmount);
    }
    else {
      return "$0.00";
    }
  }, []);

  const handleOrderListNavigate = (orderNumber: String) => {
    navigate("/order_list", { state: { orderNumber: orderNumber } });
  };

  const handleOrderDetailsNavigate = (data: any) => {
    const orderData = {
      orderId: data?.orderId,
      orderNumber: data?.orderNumber,
      productName: data?.productName,
      practiceName: data?.practiceName,
      po: data?.poNumber,
      dateOfService: data?.dateOfService,
      shippingCarrier: data?.shippingCarrier,
      trackingNumber: data?.trackingNumber,
      orderStatus: data?.orderStatus,
      provider: data?.providerName,
      patientName: data?.patientName,
      manufacturerName: data?.mfgName,
      orderDocumentId: data?.orderDocumentId,
      productCategory: data?.productCategory,
      patientId: data?.patientId, //CS-1560 - Passing patientId to order details page to display CMS form
      actualShippingMethod: data?.actualShippingMethod, //CS-1766
      requestedShippingMethod: data?.requestedShippingMethod, //CS-1766
      //CS-1564 - Passing inactive dates to order details page to hide actions for inactive products
      manufacturerInactiveDate: data?.manufacturerInactiveDate,
      productInactiveDate: data?.productInactiveDate,
    }
    navigate("/details/1", { state: { orderDetails: orderData } });

  }
  //CS-1512 - Navigating user to payment list page
  const handlePaymentListNavigate = (data: any) => {
    const Data = {
      invoiceNumber: data?.mfgInvoiceNumber,
    }
    navigate("/payment_list", { state: { paymentListSearchCriteria: Data } });

  }
  const handleDownloadMFGInvoice = async (data: any) => {
    let documentId = data?.invoiceDocumentId;
    const actionResult = await dispatch(fetchOrderDocumentsByIds(documentId));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName: `Invoice-${data?.productName}`,
        fileName: `MFG_${data?.mfgInvoiceNumber}`,
      });
    };
  }

  //CS-1322 - Function to genrate the invoice pdf
  const handleGenrateInvoice = async (invoiceId: number) => {
    const actionResult = await dispatch(previewInvoice({ invoiceId, interceptorOption: { skipErrorPopup: true } }));
    if (actionResult.payload.code === SUCCESS_CODE) {
      const documentData = actionResult.payload;
      if (documentData.data && documentData.data.content) {
        setShowPDF({
          isShow: true,
          productName: `Invoice-${selectedInvoice?.productName}`,
          content: documentData.data.content,
          fileName: `MFG_${selectedInvoice?.mfgInvoiceNumber}`,
        });
      }
    } else {
      dispatch(setSingleAlertObj({
        message: actionResult.payload.message,
        alertType: "error",
      }));
    }
  }

  const customPaymentCommentsComponent = (paymentInfo: {
    fileId?: number;
    paymentAmount?: number;
    paymentComments: any;
    paymentDate?: string;
    paymentDocumentId?: number;
    paymentId?: number;
  }) => {
    return (
      <>
        <Grid item>
          {paymentInfo?.paymentComments && (
            <CustomCommentPreview
              title="Payment Comments"
              commentBody={paymentInfo?.paymentComments}
            />
          )}
        </Grid>
      </>
    );
  };

  const orderItemStatus = useCallback((row: any) => {
    if (row?.tissueTrackingId) {
      return `Applied (#${row.tissueTrackingId})`;
    } else if (row?.productReturnId) {
      // return `Returned (#${row.productReturnId})`;
      //CS-1164 START
      if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_INITIATED) {
        return `Return Initiated (#${row.productReturnId})`;
      } else if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_PENDING) {
        return `Return Pending (#${row.productReturnId})`;
      } else if (row.productReturnStatus === ORDER_AVAILABILITY.RETURNED) {
        return `Returned (#${row.productReturnId})`;
      } else if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_REJECTED) {
        // return `Available (Return Rejected)`;
        return `Available`;
      }
      //CS-1164 END
    } else if (row?.invoiceId) {
      return "Available";
    }
    return "Pending";
  }, []);

  //CS-1212 - Start
  const removeDataUriPrefix = (dataUri: string): string => {
    const base64String = dataUri.split(",")[1];
    return base64String || "";
  };

  const handleNotDocumentFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      const files = event.target.files;
      if (files && files.length > 0) {
        const file = files[0];
        const base64File = await convertBase64(file);

        setValue("attachment", removeDataUriPrefix(String(base64File)));
        setValue("documentName", file.name);
        setValue("fileType", file.type);
        setValue("noteType", "INVOICE ADDITIONAL NOTE");
        setValue("xrefTable", "INVOICE");
        setSelectedFileName(file.name);
      } else {
        console.error("No files selected or invalid input.");
      }
    } catch (error) {
      console.error("Error processing the file:", error);
    }
  };

  const resetData = () => {
    setValue("attachment", "");
    setValue("xrefTable", "INVOICE");
    setValue("noteType", "INVOICE ADDITIONAL NOTE");
    setValue("content", "");
    handleRemoveFile();
  };

  const handleRemoveFile = () => {
    setSelectedFileName(null);
    setValue("attachment", "");
  };

  const onSubmit = async (data: InvoiceNotes) => {
    setValue("xrefId", invoiceId.toString());
    try {
      const response = await dispatch(uploadInvoiceNotes(data));
      if (
        response.payload &&
        response.payload.message === "Note is added successfully"
      ) {
        let alertMessage = "Note saved Successfully!";
        const alertObj: SingleAlertInfo = {
          message: alertMessage,
          alertType: "success",
        };
        dispatch(setSingleAlertObj(alertObj));
        dispatch(fetchInvoiceNotesById(invoiceId));
        resetData();
      }
    } catch (error) {
      console.log("Error saving note:", error);
    }
  };

  const handleActionPreview = async (row: any, type: string) => {
    const documentId = type === "note" ? row.attachementId : row.documentId;
    if (documentId) {
      const actionResult = await dispatch(
        fetchOrderDocumentsByIds(documentId)
      );
      const documentData = actionResult.payload;
      if (documentData && documentData.content) {

        setPDFContent(documentData.content);
        console.log("documentData", documentData);
        console.log("pdfContent", pdfContent);
        setShowPDF({
          isShow: true,
          productName: "Invoice - " + row.auditUserName,
          fileName: documentData.documentName,
        });
      } else {
        dispatch(
          setSingleAlertObj({
            message: "Requested Document has not found under server location!",
            alertType: "info",
          })
        );
      }
    }
  };
  //CS-1212 - End

  const showExcelPopupData = (event: React.MouseEvent<HTMLElement>) => {
    setShowExcelPopup(true)
  };

  const orderItemRow: any = useMemo(() => {
    let orderItems_: any = [];
    if (orderItemsListInfo) {
      orderItems_ = orderItemsListInfo
        .filter(item => item.invoiceId !== null)
        .map((item: any) => {
          return {
            orderItemId: item.orderItemId,
            productName: item.productName,
            size: item.size,
            quantity: item.quantity,
            units: item.units,
            serialNumber: item.serialNumber,
            orderStatus: orderItemStatus(item),
            //CS-1560 : Displaying orderitem price from backend
            cogs: item.orderItemPrice,
            // cogs : item.hasSKUPrice ? item.extendedPrice : item.extendedPrice * item.units,
            productReturnId: item.productReturnId,
            tissueTrackingId: item.tissueTrackingId,
            invoiceId: item.invoiceId,
          };
        })
        .sort((a, b) => a.units - b.units);
    }
    return orderItems_;
  }, [orderItemsListInfo, location?.state?.orderDetails?.orderStatus]);

  //CS-1458 - Function to handle checkout session
  //CS-1576 - Enabling the partial payment in stripe
  const handleCheckout = async (invoiceId: number, paymentMethodType: string, paymentType: string, paymentAmount: number) => {
      const data = {
      returnPath: location.pathname + location.search,
      invoiceId: invoiceId,
      paymentMethod: paymentMethodType,
      invoiceLineItemList: [
        {
          invoiceId: invoiceId,
          paymentAmt: paymentAmount,
          paymentType: paymentType,
        }
      ],
    };
    await dispatch(stripeCheckOutPaymentData(data)).then((res) => {
      //CS-1563
      if (res && res?.payload) {
        // Check if there are errors in the response
        if (res.payload.errors && res.payload.errors.length > 0) {
          const errorMessage = res.payload.errors[0].errorMessage;
          const alertObj: SingleAlertInfo = {
            message: errorMessage,
            alertType: "error",
          };
          dispatch(setSingleAlertObj(alertObj));
        } else if (res.payload.data) {
          const checkoutUrl = res.payload.data.checkoutUrl;
          if (checkoutUrl) window.location.href = checkoutUrl;
        }
      }
    }).catch((error) => {
      console.log(error);
    });
  };

  //CS-1512 - added failure reason popup
  const showFailureReasonPopup = (data: any) => {
    const title = "Failure Reason";
    const body = data.paymentErrorReason;
    setFailureReasonData({ title, body });
    setShowReasonPopup(true);
  };
  
  // Show cancellation reason popup
  const showCancellationReasonPopup = (data: any) => {
    const title = "Cancellation Reason";
    const body = data.paymentErrorReason;
    setFailureReasonData({ title, body });
    setShowReasonPopup(true);
  };
  
  const hideReasonPopup = () => {
    setShowReasonPopup(false);
  };

  const handleConfirm = (data: any) => {
    if (invoiceObject?.invoiceId) {
      dispatch(addLateFeeByInvoice({
        invoiceId: invoiceObject?.invoiceId,
        lateFee: data.lateFee,
        skipLoader: false
      })).then(() => {
        setInvoiceDetailsObj(invoiceObject?.invoiceId);
      })
      setIsPopupOpen(false);
    }
  };

  const balanceLabelSx = { fontSize: "0.82rem", color: mTokens.textSecondary };
  const balanceValueSx = { fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 600 };
  const graftLabelSx = { fontSize: "0.68rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase" as const, letterSpacing: "0.06em", mb: 0.3 };
  const graftValueSx = { fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 600 };
  const invoiceStatusChipColor =
    selectedInvoice?.invoiceStatus === "PAID"
      ? "#28a745"
      : selectedInvoice?.invoiceStatus === "PARTIAL"
        ? "#D97706"
        : selectedInvoice?.invoiceStatus === "PROCESSING"
          ? "#5dabff"
          : selectedInvoice?.invoiceStatus === "CLOSED"
            ? "#6b7280"
            : "#dc3545";

  return (
    <>
      {showLateFeeSummaryPopup && (
        <LateFeeSummaryPopup
          open={showLateFeeSummaryPopup}
          onClose={() => setShowLateFeeSummaryPopup(false)}
          selectedInvoice={selectedInvoice}
          lateFeeHistory={(lateFeeHistory as LateFeeHistoryDTO[]) || []}
        />
      )}
      {/* CS-1512 - added failure reason popup */}
      {showReasonPopup && (
        <CustomCommentPopup
          open={showReasonPopup}
          onClose={hideReasonPopup}
          title={failureReasonData.title}
          commentBody={failureReasonData.body}
        />
      )}
      {/* CS-1458 - Payment Popup Component  */}
      {showStripePaymentTypePopup && (
        <StripePaymentTypePopup
          open={showStripePaymentTypePopup}
          onClose={() => { setShowStripePaymentTypePopup(false); setIsStripePaymentTypePopupFromBillingDetails(false); }}
          invoiceData={{
            ...selectedInvoice,
            outstandingAmt: remainingAmount
          }}
          isFromBillingDetails={isStripePaymentTypePopupFromBillingDetails} //CS-1576
          createCheckoutSession={handleCheckout}
        />
      )}
      {showCreditMemoPopup &&
        <CreditMemoTable
          open={showCreditMemoPopup}
          recordCreditMemo={recordCreditMemo}
          // userId={userID}
          onClose={() => {
            setShowCreditMemoPopup(false);
          }}
        />
      }
      {showPDF && <PDFViewer
        isOpen={showPDF.isShow}
        onClose={() => { setShowPDF({ isShow: false, productName: "", fileName: "", content: "" }); dispatch(resetOrderDocumentInfo()); }}
        title={showPDF.productName}
        fileName={showPDF.fileName}
        base64String={orderDocumentInfo?.content || showPDF.content || ""} // CS-1322
      />}
      {isVoicePopupOpen && (
        <SpeechToTextPopup
          open={isVoicePopupOpen}
          onClose={() => { setIsVoicePopupOpen(false) }}
          onSubmitVoice={(data: any) => setValue("content", data)}
        />
      )}
      <Box
        sx={{
          mx: embedded ? 0 : { xs: "10px", sm: "20px" },
          mt: embedded ? 0 : "12px",
          pb: "72px",
        }}
      >
        {/* CS-1191 - Start */}
        <Box mb={2} display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
          {embedded ? (
            <Box data-testid="header" display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <Typography
                component="h2"
                sx={{
                  fontSize: "1.35rem",
                  fontWeight: 800,
                  color: mTokens.textPrimary,
                  lineHeight: 1.2,
                }}
              >
                Invoice Detail
                {selectedInvoice?.mfgInvoiceNumber ? (
                  <Typography
                    component="span"
                    sx={{ ml: 1, fontSize: "1.05rem", fontWeight: 700, color: "#22c55e" }}
                  >
                    #{selectedInvoice.mfgInvoiceNumber}
                  </Typography>
                ) : null}
              </Typography>
              {selectedInvoice?.invoiceStatus ? (
                <Chip
                  label={selectedInvoice.invoiceStatus}
                  size="small"
                  sx={{
                    backgroundColor: `${invoiceStatusChipColor}1a`,
                    color: invoiceStatusChipColor,
                    border: `1px solid ${invoiceStatusChipColor}55`,
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    height: 24,
                  }}
                />
              ) : null}
            </Box>
          ) : (
          <Box display="flex" alignItems="flex-start" gap={1.5}>
            <PageBackButton
              tooltip={null}
              onClick={() =>
                location.state?.fromStripeCancel
                  ? navigate("/invoice_list")
                  : navigate(-1)
              }
            />
            <Box data-testid="header">
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>
                Billing / Invoice Detail
              </Typography>
              <Typography component="h1" sx={{ fontSize: "1.6rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.1 }}>
                Invoice Detail
              </Typography>
            </Box>
          </Box>
          )}
          <Box display="flex" alignItems="center" sx={{ justifyContent: { xs: "flex-start", md: "flex-end" }, flexWrap: "wrap", rowGap: 1 }} gap={1}>
            {(selectedInvoice?.purchaseAndBill === "Y" || selectedInvoice?.invoiceDocumentId) && (
              <Button
                variant="outlined"
                removeUpperCase
                onClick={() =>
                  selectedInvoice?.invoiceDocumentId
                    ? handleDownloadMFGInvoice(selectedInvoice)
                    : handleGenrateInvoice(selectedInvoice?.invoiceId)
                }
                sx={{ ...outlineAccentBtnSx, width: { xs: "100%", sm: "auto" } }}
                startIcon={<PictureAsPdfOutlined fontSize="small" />}
              >
                Invoice PDF
              </Button>
            )}
            {canShowUpdateInvoice(selectedInvoice, canAccessEditInvoiceAction) && (
              <Button
                variant="outlined"
                removeUpperCase
                onClick={() =>
                  navigate("/edit_invoice", { state: invoiceId })
                }
                sx={{ ...outlineAccentBtnSx, width: { xs: "100%", sm: "auto" } }}
                startIcon={<ModeOutlined fontSize="small" />}
              >
                Update Invoice
              </Button>
            )}
            {SalesDetailsInfo?.[0]?.invoiceNum && selectedInvoice?.invoiceId === Number(SalesDetailsInfo[0].invoiceId) && canViewExcelData && (
              <Button
                variant="outlined"
                removeUpperCase
                sx={{ ...outlineAccentBtnSx, whiteSpace: "nowrap", mr: 1 }}
                onClick={showExcelPopupData}
                startIcon={<img src={require("../../../assets/excel_icon.png")} alt="Excel Icon" style={{ width: 24, height: 24 }} />}
              >
                View Excel Data
              </Button>
            )}
            <Box display="flex" alignItems="center" sx={{ justifyContent: { xs: "flex-start", md: "flex-end" } }} gap={1}>
              <ActionMenu label="Action" actions={actions} disabled={!hasVisibleActions} removeUpperCase sx={outlineAccentBtnSx} />
            </Box>
          </Box>
        </Box>
        {/* CS-1191 - End */}

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr" }, gap: 2, alignItems: "flex-start" }}>
          {/* LEFT: Grafts + Notes */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, order: { xs: 2, md: 1 } }}>
            <Box sx={cardSx}>
              <SectionHeader icon={<ViewInArOutlined fontSize="small" />} title="Grafts" />
              <Box sx={{ px: 2.8, py: 1.8 }}>
                {orderItemRow?.map((row: any, index: any) => (
                  <Box key={index}>
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      sx={{
                        flexWrap: {
                          xs: "wrap",
                          sm: "nowrap"
                        },
                        overflowX: "auto",
                        padding: "5px 0px 10px 0px"
                      }}
                    >
                      <Grid item md={3} xs={3}>
                        <Typography sx={graftLabelSx}>Products</Typography>
                        <Typography sx={graftValueSx}>{row.productName}</Typography>
                      </Grid>
                      <Grid item md={2} xs={3}>
                        <Typography sx={graftLabelSx}>Ordered Size</Typography>
                        <Typography sx={graftValueSx}>{row.size}</Typography>
                      </Grid>
                      {row.quantity != null && (
                        <Grid item md={1} xs={2}>
                          <Typography sx={graftLabelSx}>QTY</Typography>
                          <Typography sx={graftValueSx}>{(row.quantity)}</Typography>
                        </Grid>
                      )}
                      <Grid item md={2} xs={5}>
                        <Typography sx={graftLabelSx}>Serial Number</Typography>
                        <Typography sx={{
                          ...graftValueSx,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}>{row.serialNumber}</Typography>
                      </Grid>
                      <Grid
                        item
                        md={3}
                        xs={4}
                        sx={{
                          textAlign: {
                            xs: "left",
                            sm: "center"
                          },
                        }}
                      >
                        <Typography sx={graftLabelSx}>Usage Status</Typography>
                        <Chip
                          label={row.orderStatus}
                          size="small"
                          sx={{
                            backgroundColor: isDarkModal ? "#2C3E50" : mTokens.headerFooterBg,
                            padding: "1px 10px",
                            fontWeight: "bold",
                            border: `1px solid ${mTokens.border}`,
                            color: mTokens.textPrimary,
                          }}
                        />
                      </Grid>
                      <Grid item md={2} xs={5}>
                        <Typography sx={graftLabelSx}>Practice Price</Typography>
                        <Typography
                          sx={{
                            ...graftValueSx,
                            textDecoration: row.productReturnId && row.productReturnStatus === ORDER_AVAILABILITY.RETURNED ? "line-through red" : "none",
                            textDecorationThickness: row.productReturnId ? "2px" : "initial",
                          }}>{priceFormatter(row.cogs)}</Typography>
                      </Grid>
                    </Grid>
                    {index < orderItemRow.length - 1 && (
                      <Box
                        sx={{
                          borderBottom: "1px dashed",
                          borderColor: mTokens.border,
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* CS-1212 - Start */}
            <Box sx={cardSx}>
              <SectionHeader icon={<ChatBubbleOutlineOutlined fontSize="small" />} title="Notes" />
              <Box
                sx={{
                  maxHeight: "400px",
                  overflowY: "auto",
                }}
              >
                {invoiceNotesList?.map((note: any, index: number) => {
                  if (!(note.auditUserName in userSideMap)) {
                    userSideMap[note.auditUserName] =
                      lastAuditUserName === null ||
                      !userSideMap[lastAuditUserName];
                  }
                  const isLeft = userSideMap[note.auditUserName];
                  lastAuditUserName = note.auditUserName;

                  return (
                    <Box
                      key={index}
                      display="flex"
                      justifyContent={isLeft ? "flex-start" : "flex-end"}
                      p={2}
                    >
                      {isLeft && (
                        <Avatar
                          sx={{
                            marginRight: 1,
                            fontSize: 20,
                            fontWeight: "bold",
                            backgroundColor: mTokens.accent,
                          }}
                        >
                          {note.auditUserName.charAt(0)}
                        </Avatar>
                      )}
                      <Box
                        sx={{
                          padding: 1.4,
                          backgroundColor: isDarkModal ? "#1c252e" : mTokens.headerFooterBg,
                          minWidth: "30%",
                          maxWidth: "100%",
                          borderRadius: isLeft
                            ? "1px 10px 10px 10px"
                            : "10px 1px 10px 10px",
                          border: `1px solid ${mTokens.border}`,
                        }}
                      >
                        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: mTokens.textPrimary }}>
                          {note.auditUserName}
                        </Typography>
                        <Typography
                          sx={{ fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 600, mt: 1, whiteSpace: "pre-line" }}
                        >
                          {note.content}
                        </Typography>

                        {note.attachementId && (
                          <Stack
                            alignItems="center"
                            direction="row"
                            gap={1}
                            onClick={() => handleActionPreview(note, "note")}
                            mt={1}
                            sx={{ cursor: "pointer", color: mTokens.accent }}
                          >
                            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>
                              {note.documentName}
                            </Typography>
                            <Tooltip title="View File" placement="top" arrow>
                              <Box display="flex">
                                <DocumentScannerOutlined sx={{ fontSize: 22 }} />
                              </Box>
                            </Tooltip>
                          </Stack>
                        )}
                        <Box display="flex" justifyContent="flex-end">
                          <Typography sx={{ fontSize: "0.7rem", color: mTokens.textSecondary, mt: 0.5 }}>
                            {note.auditTimestamp}
                          </Typography>
                        </Box>
                      </Box>

                      {!isLeft && (
                        <Avatar
                          sx={{
                            marginLeft: 1,
                            fontSize: 20,
                            fontWeight: "bold",
                          }}
                        >
                          {note.auditUserName.charAt(0)}
                        </Avatar>
                      )}
                    </Box>
                  );
                })}
              </Box>
              <Divider sx={{ borderColor: mTokens.border }} />
              <Box
                sx={{
                  p: 2,
                  backgroundColor: mTokens.headerFooterBg,
                }}
              >
                <Box
                // style={{ display: `${hideElement(!canNoteIVR)}` }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 1,
                      py: 0.5,
                      minHeight: 44,
                      borderRadius: "12px",
                      border: `1px solid ${mTokens.border}`,
                      backgroundColor: mTokens.bg,
                    }}
                  >
                    <Tooltip title="Speech to text" placement="top" arrow>
                      <IconButton
                        color="default"
                        component="label"
                        sx={{
                          width: 36,
                          height: 36,
                          p: 0,
                          borderRadius: "10px",
                          bgcolor: mTokens.accent,
                          transition: "background-color 0.4s ease",
                          flexShrink: 0,
                          "&:hover": {
                            bgcolor: "#0d9488",
                          },
                        }}
                        onClick={() => setIsVoicePopupOpen(true)}
                      >
                        <KeyboardVoice sx={{ color: "#ffffff", fontSize: "1.15rem" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Attach File" placement="top" arrow>
                      <IconButton
                        color="default"
                        component="label"
                        sx={{
                          width: 36,
                          height: 36,
                          p: 0,
                          borderRadius: "10px",
                          border: `1px solid ${mTokens.border}`,
                          backgroundColor: mTokens.headerFooterBg,
                          color: mTokens.textSecondary,
                          flexShrink: 0,
                          "&:hover": {
                            borderColor: mTokens.accent,
                            color: mTokens.accent,
                            backgroundColor: `${mTokens.accent}0d`,
                          },
                        }}
                      >
                        <AttachFile sx={{ fontSize: "1.15rem" }} />
                        <input
                          type="file"
                          hidden
                          onChange={handleNotDocumentFileChange}
                        />
                      </IconButton>
                    </Tooltip>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                        "& .MuiFormControl-root": { m: 0, width: "100%" },
                        "& .MuiInputBase-root.MuiInputBase-multiline": {
                          padding: "0 !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          alignItems: "center",
                          minHeight: "36px !important",
                          padding: "0 !important",
                          backgroundColor: "transparent !important",
                          boxShadow: "none !important",
                          borderRadius: "8px !important",
                        },
                        "& .MuiOutlinedInput-root fieldset, & .MuiOutlinedInput-notchedOutline": {
                          border: "none !important",
                        },
                        "& .MuiInputLabel-root, & label": { display: "none" },
                        "& .MuiOutlinedInput-root textarea, & .MuiInputBase-inputMultiline": {
                          boxSizing: "border-box !important",
                          minHeight: "36px !important",
                          height: "auto !important",
                          maxHeight: "96px !important",
                          padding: "8px 10px !important",
                          lineHeight: "20px !important",
                          fontSize: "0.88rem",
                          color: mTokens.textPrimary,
                          resize: "none",
                          overflowY: "auto",
                        },
                      }}
                    >
                      <CustomTextField
                        controllerName="content"
                        control={control}
                        label=""
                        placeholder="Write a note..."
                        multiline
                        minRows={1}
                        maxRows={4}
                        size="small"
                        rules={{ required: "Notes are required." }}
                        inputProps={{ "aria-label": "Notes" }}
                      />
                    </Box>
                    <IconButton
                      sx={{
                        width: 36,
                        height: 36,
                        p: 0,
                        borderRadius: "10px",
                        backgroundColor: mTokens.accent,
                        flexShrink: 0,
                        "&:hover": {
                          backgroundColor: "#0d9488",
                        },
                      }}
                      onClick={handleSubmit(onSubmit)}
                    >
                      <Send sx={{ color: "#ffffff", fontSize: "1.15rem" }} />
                    </IconButton>
                  </Box>
                  {selectedFileName && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        mt: 1.2,
                        px: 1.2,
                        py: 0.6,
                        borderRadius: "999px",
                        border: `1px solid ${mTokens.accent}55`,
                        backgroundColor: `${mTokens.accent}14`,
                        color: mTokens.accent,
                        maxWidth: "100%",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontStyle: "italic",
                          mr: 0.5,
                          color: mTokens.textPrimary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {selectedFileName}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleRemoveFile}
                        sx={{
                          color: mTokens.error,
                        }}
                      >
                        <CancelOutlined fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            {/* CS-1212 - End */}
          </Box>

          {/* RIGHT: Invoice Info + Balance + Payments */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, order: { xs: 1, md: 2 } }}>
            <Box sx={cardSx}>
              <SectionHeader icon={<ReceiptLongOutlined fontSize="small" />} title="Invoice Info" />
              <Box sx={{ px: 2.8, py: 1 }}>
                <InfoRow
                  label="Invoice"
                  mTokens={mTokens}
                  value={
                    <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                      <span>{selectedInvoice?.mfgInvoiceNumber}</span>
                      {selectedInvoice?.isUpfrontOrder && (
                        <BoltOutlined
                          sx={{ fontSize: 20, color: "#00E5B0", flex: "0 0 auto" }}
                          titleAccess="Upfront Invoice"
                        />
                      )}
                      {/* CS-1765 - Use top-level purchaseAndBill from getInvoiceById response instead of manufactureCustomConfig */}
                      {selectedInvoice?.purchaseAndBill === "Y" ? (
                        <CustomIconButton
                          tooltipPlacement="top"
                          tooltipMessage="Preview"
                          color="info"
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            selectedInvoice?.invoiceDocumentId ? handleDownloadMFGInvoice(selectedInvoice) : handleGenrateInvoice(selectedInvoice?.invoiceId)}
                        >
                          <AssignmentOutlined fontSize="small" />
                        </CustomIconButton>
                      ) : (<>
                        {selectedInvoice?.invoiceDocumentId && (<>
                          <CustomIconButton
                            tooltipPlacement="top"
                            tooltipMessage="Preview"
                            color="info"
                            size="small"
                            variant="outlined"
                            onClick={() => handleDownloadMFGInvoice(selectedInvoice)}
                          >
                            <AssignmentOutlined fontSize="small" />
                          </CustomIconButton>
                        </>)}
                      </>)}
                    </Box>
                  }
                />
                {selectedInvoice?.purchaseAndBill === "Y" && (
                  <InfoRow label="Invoice Reference Id" value={selectedInvoice?.invReferenceId || "-"} mTokens={mTokens} />
                )}
                <InfoRow label="MFG" value={selectedInvoice?.mfgName} mTokens={mTokens} />
                <InfoRow
                  label="Status"
                  mTokens={mTokens}
                  value={
                    <Box
                      component="span"
                      sx={{
                        color: selectedInvoice?.invoiceStatus === "PAID" ? "#28a745" : mTokens.error,
                      }}
                    >
                      {selectedInvoice?.invoiceStatus || "-"}
                    </Box>
                  }
                />
                <InfoRow label="Product" value={selectedInvoice?.productName} mTokens={mTokens} />
                <InfoRow
                  label="Due Date"
                  mTokens={mTokens}
                  value={
                    <Box
                      component="span"
                      sx={{
                        color: selectedInvoice?.invoiceStatus === "PAID" ? "#28a745" : mTokens.error,
                      }}
                    >
                      {selectedInvoice?.invoiceDueDate || "-"}
                    </Box>
                  }
                />
                <InfoRow label="Practice" value={selectedInvoice?.practiceName} mTokens={mTokens} />
                <InfoRow
                  label="Aging"
                  mTokens={mTokens}
                  value={
                    <Box component="span" sx={{ color: mTokens.error }}>
                      {renderDynamicAgingColumn(selectedInvoice)}
                    </Box>
                  }
                />
                <InfoRow
                  label="Order"
                  mTokens={mTokens}
                  value={
                    <Box component="span">
                      {selectedInvoice?.orderNumber}{" "}
                      {selectedInvoice?.orderId && (
                        <Link
                          component="button"
                          underline="hover"
                          variant="body2"
                          onClick={() => handleOrderDetailsNavigate(selectedInvoice)}
                          sx={{ color: mTokens.accent }}
                        >
                          (Order Details)
                        </Link>
                      )}
                    </Box>
                  }
                />
                {/* CS-1676 - Added ordered for label */}
                <InfoRow label="Ordered For" value={selectedInvoice?.patientName} mTokens={mTokens} />
              </Box>
            </Box>

            <Box sx={cardSx}>
              <SectionHeader icon={<AccountBalanceWalletOutlined fontSize="small" />} title="Balance" />
              <Box sx={{ px: 2.8, pt: 0.5, pb: 1.5 }}>
                <Box display="flex" alignItems="center" py={0.45} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
                  <Typography sx={{ ...balanceLabelSx, width: "45%", flexShrink: 0 }}>Invoice Amt</Typography>
                  <Typography sx={{ ...balanceValueSx, flex: 1 }}>{invoiceAmount && priceFormatter(invoiceAmount) || priceFormatter(0)}</Typography>
                </Box>
                {selectedInvoice?.isUpfrontOrder && selectedInvoice?.discountAmount && selectedInvoice.discountAmount > 0.005 ? (
                  <Box display="flex" alignItems="center" py={0.45} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
                    <Typography sx={{ ...balanceLabelSx, width: "45%", flexShrink: 0 }}>
                      Discount{selectedInvoice.discount != null && selectedInvoice.discount > 0 ? ` (${selectedInvoice.discount}%)` : ""}
                    </Typography>
                    <Typography sx={{ ...balanceValueSx, flex: 1, color: "#00E5B0" }}>
                      {"−"}{priceFormatter(selectedInvoice.discountAmount)}
                    </Typography>
                  </Box>
                ) : null}
                <Box display="flex" alignItems="center" py={0.45} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
                  <Typography sx={{ ...balanceLabelSx, width: "45%", flexShrink: 0 }}>Shipping Charges</Typography>
                  <Typography sx={{ ...balanceValueSx, flex: 1 }}>{shippingCharges && priceFormatter(shippingCharges) || priceFormatter(0)}</Typography>
                </Box>
                <Box display="flex" alignItems="center" py={0.45} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
                  <Typography sx={{ ...balanceLabelSx, width: "45%", flexShrink: 0 }}>Late Fees</Typography>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={balanceValueSx}>{lateFees && priceFormatter(lateFees) || priceFormatter(0)}</Typography>
                    <Link
                      component="button"
                      underline="hover"
                      variant="body2"
                      onClick={handleLateFeeSummaryClick}
                      sx={{ color: mTokens.accent }}
                    >
                      (Late Fee Summary)
                    </Link>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" py={0.45} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
                  <Typography sx={{ ...balanceLabelSx, width: "45%", flexShrink: 0, fontWeight: 700, color: mTokens.textPrimary }}>Total</Typography>
                  <Typography sx={{ color: "#5BE49B", fontWeight: "bold", fontSize: "0.88rem", flex: 1 }}>{total && priceFormatter(total) || priceFormatter(0)}</Typography>
                </Box>
                <Box display="flex" alignItems="center" py={0.45} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
                  <Typography sx={{ ...balanceLabelSx, width: "45%", flexShrink: 0 }}>Credits</Typography>
                  <Typography sx={{ ...balanceValueSx, flex: 1 }}>
                    {selectedInvoice?.creditAmount ?
                      <Link
                        component="button"
                        underline="hover"
                        variant="body2"
                        onClick={() => setShowCreditMemoPopup(true)}
                        sx={{ color: mTokens.accent }}
                      >
                        {priceFormatter(selectedInvoice?.creditAmount)}
                      </Link>
                      : priceFormatter(0)}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" py={0.45} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
                  <Typography sx={{ ...balanceLabelSx, width: "45%", flexShrink: 0 }}>Payments</Typography>
                  <Typography sx={{ ...balanceValueSx, flex: 1 }}>{paidAmount && priceFormatter(paidAmount) || priceFormatter(0)}</Typography>
                </Box>
                <Box display="flex" alignItems="center" py={0.45} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
                  {/* CS-1458 - Label change */}
                  <Typography sx={{ ...balanceLabelSx, width: "45%", flexShrink: 0 }}>Outstanding Amount</Typography>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={balanceValueSx}>
                      {remainingAmount && priceFormatter(remainingAmount) || priceFormatter(0)}
                    </Typography>
                    {((selectedInvoice?.manufacturerStripeConfig?.stripe_integration === "Y" && canCreateStripePayment) || (selectedInvoice?.manufacturerStripeConfig?.stripe_integration === "N" && selectedInvoice?.hasPaymentInstruction)) && (
                      <Button
                        tooltipMessage={selectedInvoice?.manufacturerStripeConfig?.stripe_integration === 'Y' ? "Pay via Stripe" : "Payment Instruction"}
                        color={selectedInvoice?.manufacturerStripeConfig?.stripe_integration === 'Y' ? undefined : "warning"}
                        variant='outlined'
                        size="small"
                        startIcon={<AttachMoney />}
                        style={{
                          display: `${hideElement(selectedInvoice?.invoiceStatus === 'PAID' || selectedInvoice?.invoiceStatus === 'CLOSED')}`,
                          // CS-1682 - Stripe purple color optimized for visibility on highlighted rows
                          ...(selectedInvoice?.manufacturerStripeConfig?.stripe_integration === 'Y' && {
                            color: "white",
                            borderColor: theme.palette.mode === 'dark' ? "#c374ff" : "#c374ff",
                            background: theme.palette.mode === 'dark'
                              ? "linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7, #ec4899)"
                              : "linear-gradient(135deg, #4f46e5, #7c3aed, #9333ea, #db2777)",
                          })
                        }}
                        onClick={() => {
                          //CS-1458 - Handling zero outstanding amount case for Pay Now
                          if (selectedInvoice?.manufacturerStripeConfig?.stripe_integration === "Y") { //CS-1651 
                            if (remainingAmount && Number(remainingAmount) > 0) {
                              setShowStripePaymentTypePopup(true);
                              setIsStripePaymentTypePopupFromBillingDetails(true)
                            } else {
                              let errObj: SingleAlertInfo = {
                                message: "This invoice currently shows ‘Unpaid,’ but the outstanding amount due is 0. It was likely settled by credits or adjustments, so no payment is needed.",
                                alertType: "error"
                              };
                              dispatch(setSingleAlertObj(errObj));
                            }
                            // CS-1651 start
                          } else if (selectedInvoice?.hasPaymentInstruction) {
                            if (selectedInvoice?.paymentInstructionType === 'Product Document') {
                              if (isPaymentInstructionUrl(selectedInvoice?.paymentInstructionValue)) {
                                window.open(selectedInvoice.paymentInstructionValue, '_blank');
                              } else if (selectedInvoice?.paymentInstructionValue) {
                                handleOpenPaymentInstructionDoc(selectedInvoice);
                              }
                            } else if (selectedInvoice?.paymentInstructionType === 'External URL' && selectedInvoice?.paymentInstructionValue) {
                              window.open(selectedInvoice.paymentInstructionValue, '_blank');
                            }
                            // CS-1651 end
                          }
                        }}
                      >
                        Pay Now
                      </Button>
                    )}
                  </Box>
                </Box>

                <Box sx={{ pt: 1 }}>
                  <CustomProgress
                    filledValue={paidAmount}
                    total={(total - (selectedInvoice?.creditAmount || 0))}
                    color="warning"
                    height={10}
                  />
                </Box>
              </Box>
            </Box>

            {paymentListData.length > 0 && (
              <Box sx={cardSx}>
                <SectionHeader
                  icon={<PaymentsOutlined fontSize="small" />}
                  title="Payments"
                  action={
                    canAccessPaymentDetails ? (
                      <Typography variant="subtitle1" component="div">
                        {selectedInvoice?.payment && selectedInvoice?.payment?.paymentInfo?.length > 0 && (
                          <Link
                            component="button"
                            underline="hover"
                            onClick={() => handlePaymentListNavigate(selectedInvoice)}
                            sx={{ color: mTokens.accent }}
                          >
                            (Payment Details)
                          </Link>
                        )}
                      </Typography>
                    ) : undefined
                  }
                />
                <Box sx={{ px: 2.8, py: 1.8 }}>
                  <Grid container spacing={2}>
                    {paymentListData.map((payment: any, paymentIndex: number) => (
                      <React.Fragment key={paymentIndex}>
                        <Grid item xs={3}>
                          <Typography sx={graftLabelSx}>Pay Date</Typography>
                          <Typography sx={graftValueSx}>{payment.paymentDate}</Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography sx={graftLabelSx}>Payment Amount</Typography>
                          <Typography
                            sx={{
                              ...graftValueSx,
                              ...(payment.paymentStatus === PAYMENT_STATUS.VOID ? {
                                textDecoration: "line-through red",
                                textDecorationThickness: "2px",
                              } : {}),
                            }}
                          >{priceFormatter(payment.paymentAmount)}</Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography sx={graftLabelSx}>Status</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'left' }}>
                            <Typography sx={{ ...graftValueSx, color: renderPaymentStatusColor(payment.paymentStatus) }}>{payment.paymentStatus}</Typography>
                            {payment.paymentStatus === PAYMENT_STATUS.FAILED && (
                              <CustomIconButton
                                tooltipPlacement="top"
                                tooltipMessage="Failed Reason"
                                color="info"
                                size="small"
                                variant="outlined"
                                onClick={() => showFailureReasonPopup(payment)}
                              >
                                <CommentOutlined fontSize="small" />
                              </CustomIconButton>
                            )}
                            {payment.paymentStatus === PAYMENT_STATUS.CANCELLED && (
                              <CustomIconButton
                                tooltipPlacement="top"
                                tooltipMessage="Cancellation Reason"
                                color="info"
                                size="small"
                                variant="outlined"
                                onClick={() => showCancellationReasonPopup(payment)}
                              >
                                <CommentOutlined fontSize="small" />
                              </CustomIconButton>
                            )}
                          </Box>
                        </Grid>
                        {/* CS-1512 - Added one more column for payment method */}
                        <Grid item xs={3}>
                          <Typography sx={graftLabelSx}> Pay Method</Typography>
                          <Typography sx={graftValueSx}>
                            {payment?.paymentMethod
                              ? (payment.paymentMethod === "us_bank_account" ? "ACH" : formatMethodLabel(payment.paymentMethod))
                              : "-"}
                          </Typography>
                        </Grid>
                      </React.Fragment>
                    ))}
                  </Grid>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        {/* <CustomCommentPopup
          open={showPaymentCommentPopup}
          onClose={hideCustomPaymentCommentPopup}
          title={PaymentCommentData.title}
          commentBody={PaymentCommentData.body}
        /> */}
        <ExcelDetailsPopup onClose={() => setShowExcelPopup(false)} open={showExcelPopup} rows={SalesDetailsInfo} />
        {showOptionPopup.show && (
          <OptionsPopup
            open={showOptionPopup.show}
            onClose={() => setShowOptionsPopup({ message: "", show: false })}
            variant={showOptionPopup.variant}
            message={<>{showOptionPopup.message}</>}
            buttons={showOptionPopup.buttons}
            title={showOptionPopup.title}
            obj={showOptionPopup.obj}
          />
        )}
        <Dialog
          open={overridePayoutInvoices !== null}
          onClose={() => setOverridePayoutInvoices(null)}
          fullWidth
          maxWidth="lg"
          fullScreen={isMobile}
          sx={{ zIndex: 1600 }}
          PaperProps={{ sx: paperSx(isMobile) }}
        >
          {overridePayoutInvoices !== null ? (
            <>
              <Box sx={headerSx}>
                <Box sx={iconBoxSx(mTokens.accent)}>
                  <PaymentsOutlined sx={{ fontSize: "1.25rem", color: mTokens.accent }} />
                </Box>
                <Box sx={{ flex: 1, pr: 4 }}>
                  <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: mTokens.textPrimary, lineHeight: 1.3 }}>
                    Record Payout
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: mTokens.textSecondary, mt: 0.15 }}>
                    Pay the override service fees for the selected invoices.
                  </Typography>
                </Box>
                <IconButton onClick={() => setOverridePayoutInvoices(null)} size="small" sx={closeIconSx} aria-label="Close">
                  <CloseOutlined sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Box>
              <Box sx={bodySx}>
                <RecordPayoutOverrideForm
                  selectedInvoices={overridePayoutInvoices}
                  onCancel={() => setOverridePayoutInvoices(null)}
                  onRecorded={() => {
                    setOverridePayoutInvoices(null);
                    refreshInvoice();
                  }}
                  overlayZIndex={1600}
                />
              </Box>
            </>
          ) : null}
        </Dialog>
        <Dialog
          open={agencyPayoutInvoices !== null}
          onClose={() => setAgencyPayoutInvoices(null)}
          fullWidth
          maxWidth="lg"
          fullScreen={isMobile}
          sx={{ zIndex: 1600 }}
          PaperProps={{ sx: paperSx(isMobile) }}
        >
          {agencyPayoutInvoices !== null ? (
            <>
              <Box sx={headerSx}>
                <Box sx={iconBoxSx(mTokens.accent)}>
                  <PaymentsOutlined sx={{ fontSize: "1.25rem", color: mTokens.accent }} />
                </Box>
                <Box sx={{ flex: 1, pr: 4 }}>
                  <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: mTokens.textPrimary, lineHeight: 1.3 }}>
                    Record Payout
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: mTokens.textSecondary, mt: 0.15 }}>
                    Pay the agency service fees for the selected invoices.
                  </Typography>
                </Box>
                <IconButton onClick={() => setAgencyPayoutInvoices(null)} size="small" sx={closeIconSx} aria-label="Close">
                  <CloseOutlined sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Box>
              <Box sx={bodySx}>
                <RecordPayoutForm
                  selectedInvoices={agencyPayoutInvoices}
                  onCancel={() => setAgencyPayoutInvoices(null)}
                  onRecorded={() => {
                    setAgencyPayoutInvoices(null);
                    refreshInvoice();
                  }}
                  overlayZIndex={1600}
                />
              </Box>
            </>
          ) : null}
        </Dialog>
        <EditCommissionPopup
          open={serviceFeeEdit !== null}
          handleClose={() => setServiceFeeEdit(null)}
          mode="commission-details"
          uiVariant="billing"
          showReasonField
          disableAmountField={false}
          disableRateField
          paymentData={serviceFeeEdit?.paymentData ?? null}
          agencyData={serviceFeeEdit?.agencyData ?? null}
          onCommissionRateUpdate={handleServiceFeeUpdate}
          zIndex={1600}
        />
        <LateFeePopup
          open={isPopupOpen}
          onClose={() => {
            setIsPopupOpen(false);
          }}
          onConfirm={handleConfirm}
          lateFeeReceivedAmount={lateFees}
          currency="$"
          lateFeeAmountLabel="Outstanding Late Fee"
        />
      </Box>
    </>
  );
};

export default BillingDetail;
